/**
 * The typed confirmation for retiring a product permanently.
 *
 * `components.md`, *Typed-confirmation dialog*: *"The typed challenge is **the GTIN**, not
 * the word 'delete' — typing the number forces the user to read the identifier of the thing
 * they are destroying."*
 *
 * That is the whole design of this control and it is easy to lose in an implementation.
 * "delete" can be typed without looking; a fourteen-digit number cannot. The friction is
 * not there to slow the user down — it is there to make them **read the identifier**, which
 * is the check that catches retiring the wrong product. So the challenge is compared
 * against the GTIN and nothing else, and there is no shorter form that also passes.
 *
 * ## Why the comparison is character-by-character
 *
 * `components.md`'s input rules: *"Mark the offending character in place
 * (`--pp-pill-error-bg` behind it) rather than reddening the whole field."* A boolean
 * "matches / does not match" cannot do that. {@link checkRetireChallenge} returns the index
 * of the first character that diverges, so the field can mark exactly the digit that is
 * wrong — which on a fourteen-digit number is the difference between a fixable typo and a
 * user starting again.
 *
 * ## And why the words are what they are
 *
 * *"Retire permanently"* is the action; the terminal status reads *"No longer available"*.
 * **"tombstone" and "delete" never appear**, in this file or on screen — the storage word
 * stops at `product-row.ts`'s status map and the shopper's word is used on both audiences'
 * screens. The check that keeps this true asserts on **rendered output**, because that is
 * the claim: a grep over source cannot tell copy from a column name.
 */

import type { ValidationState } from "@packagepage/ui";

export interface RetireChallenge {
  /** The typed value is the GTIN, exactly. The confirm button is enabled on this and only this. */
  readonly satisfied: boolean;
  /**
   * Index of the first character that does not match, or `null` when what has been typed
   * so far is a correct prefix.
   *
   * A correct *prefix* is not an error — it is someone halfway through typing — so it
   * reports `null` and the field stays neutral. Reddening a field while it is being filled
   * in is how a form teaches people to ignore its validation.
   */
  readonly firstWrongIndex: number | null;
  /** Characters still to type. Zero does not imply {@link satisfied} — a longer string overshoots. */
  readonly remaining: number;
}

export function checkRetireChallenge(gtin14: string, typed: string): RetireChallenge {
  // Not trimmed, not case-folded, nothing normalised. A GTIN is fourteen digits: there is
  // no whitespace or casing in it to be lenient about, and leniency on the one control in
  // the product that cannot be undone would be spending the user's attention to save them
  // a keystroke.
  let firstWrong: number | null = null;
  for (let i = 0; i < typed.length; i += 1) {
    if (typed[i] !== gtin14[i]) {
      firstWrong = i;
      break;
    }
  }

  return {
    satisfied: typed === gtin14,
    firstWrongIndex: firstWrong,
    remaining: Math.max(0, gtin14.length - typed.length),
  };
}

/**
 * The challenge's state as the input control needs it.
 *
 * **Extracted because the alternative was untestable** (found by the test agent, c34). This
 * mapping lived inline in `RetireDialog`'s JSX, and `RetireDialog` only mounts behind an
 * `onClick` — which `renderToStaticMarkup` can never fire, and jsdom is not in this suite.
 * So the only way to cover it was a hand-copied reconstruction of the JSX in the test, which
 * the agent flagged rather than hid: a future edit here — someone adding a `fix` button —
 * would go unnoticed while the copy went on passing.
 *
 * As a pure function it is callable with any {@link RetireChallenge}, no interactivity
 * needed, and the duplication is gone. **The `fix` field is deliberately absent and its
 * absence is the point** — see {@link checkRetireChallenge}'s header: a one-click fix
 * carrying the corrected value is right on every other field in the product and wrong on
 * this one, because it would type the confirmation for the user.
 */
export function retireValidationState(
  challenge: RetireChallenge,
): ValidationState | undefined {
  if (challenge.satisfied) {
    return { state: "valid", message: "That matches this product's code" };
  }
  // A correct prefix is someone halfway through typing, not an error. Returning nothing
  // keeps the field neutral — reddening a field while it is being filled in is how a form
  // teaches people to ignore its validation.
  if (challenge.firstWrongIndex === null) return undefined;
  return {
    state: "invalid",
    rule: "The confirmation is this product's code, all fourteen digits.",
    cause:
      "Two digits transposed is the usual cause in a number this long — the marked " +
      "character is the first that differs.",
    markFrom: challenge.firstWrongIndex,
    markTo: challenge.firstWrongIndex + 1,
  };
}

/**
 * What a scan gets after the product is retired — named exactly, per the design.
 *
 * *"Names the exact page a scan will get afterwards."* This is the sentence that makes the
 * act legible: a printed code **never dead-ends** (§3.4 rung 2 — a known-then-retired GTIN
 * goes on resolving), so the consequence is not that scans break, it is that they land
 * somewhere else permanently. A dialog that led with the software fact — "this cannot be
 * undone" — would be true and would not tell the user the thing they need.
 */
export function retireConsequence(liveUrl: string): string {
  return (
    `Every scan of this code — including the labels already printed — will land on the ` +
    `platform's "no longer available" page instead of your product page, permanently. ` +
    `${liveUrl} keeps answering; what it answers with changes and cannot be changed back.`
  );
}
